// src/componentes/AppLayout/AppLayout.jsx
import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, Outlet, useLocation, Navigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { FaHeadset, FaSignOutAlt, FaBars } from "react-icons/fa";
import { useUserContext } from "../GestionDeUsuarios/UserContext";
import { firebaseConfig } from "../../Firebase/firebaseconfig";
import SidebarGroup from "./SidebarGroup";
import { SIDEBAR_ITEMS, SIDEBAR_GROUPS, defaultGroupsOpen } from "./sidebarConfig";
import "./AppLayout.css";
import logo from "../../logokye.png";
import appBg from "../../background.jpg";

const AppLayout = () => {
  const { user, can } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ── Sidebar reorg: ítems visibles según permisos, divididos en top-level
  //    y grupos colapsables. La config vive en sidebarConfig.js.
  //    OJO: todos los hooks deben ir ANTES del early-return del comisionista.
  const visibleItems = useMemo(
    () => SIDEBAR_ITEMS.filter((it) => {
      try { return it.visible(can, user); } catch { return false; }
    }),
    [can, user]
  );
  const itemsByGroup = useMemo(() => {
    const map = {};
    for (const g of SIDEBAR_GROUPS) map[g.id] = [];
    for (const it of visibleItems) {
      if (it.group && map[it.group]) map[it.group].push(it);
    }
    return map;
  }, [visibleItems]);

  // ── Estado abierto/cerrado de grupos, persistido por usuario en localStorage.
  //    Primera vez: default inteligente por rol (defaultGroupsOpen).
  const storageKey = `kye_sidebar_groups_${user?.uid || "anon"}`;
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved);
    } catch { /* ignora storage corrupto */ }
    return defaultGroupsOpen(user, can);
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(openGroups)); } catch { /* noop */ }
  }, [storageKey, openGroups]);

  const topLevelItems = visibleItems.filter((it) => !it.group);
  const toggleGroup = (groupId) =>
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));

  // Los usuarios con rol comisionista solo tienen acceso al portal /portal/mi-cuenta.
  // Si llegan al layout de admin, los redirigimos a su portal para que no queden atrapados.
  if (can("comisiones.miCuenta.ver") && !can("clientes.ver") && !can("comisiones.ver")) {
    return <Navigate to="/portal/mi-cuenta" replace />;
  }

  const handleLogout = async () => {
    await signOut(getAuth());
    navigate("/", { replace: true });
  };

  const isWide =
    location.pathname.startsWith("/app/interna") ||
    location.pathname.startsWith("/app/dashboard") ||
    location.pathname.startsWith("/app/cxp") ||
    location.pathname.startsWith("/app/cajas") ||
    location.pathname.startsWith("/app/erp") ||
    location.pathname.startsWith("/app/comisiones") ||
    location.pathname.startsWith("/app/comisionistas");

  const showAppBg = true;
  const navClass = ({ isActive }) => `nav-link ${isActive ? "active" : ""}`;
  const isPlayground = firebaseConfig.projectId === "sistema-kye-playground";
  const envLabel = isPlayground ? process.env.REACT_APP_ENV_LABEL || "PLAYGROUND" : "";

  return (
    <div className="app-layout">
      {envLabel && <div className="environment-banner">{envLabel}</div>}
      <button
        className="mobile-menu-button"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <FaBars />
      </button>

      <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <img src={logo} alt="Grupo Inmobiliario" className="sidebar-logo" />
        </div>

        <nav className="sidebar-nav">
          <ul>
            {topLevelItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <NavLink to={item.route} className={navClass}>
                    {Icon && <Icon />} {item.label}
                  </NavLink>
                </li>
              );
            })}

            {SIDEBAR_GROUPS.map((group) => (
              <SidebarGroup
                key={group.id}
                group={group}
                items={itemsByGroup[group.id] || []}
                isOpen={!!openGroups[group.id]}
                onToggle={toggleGroup}
                navClass={navClass}
              />
            ))}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <ul>
            {can("tickets.ver") && (
              <li>
                <NavLink to="/app/tickets" className={navClass}>
                  <FaHeadset /> Soporte
                </NavLink>
              </li>
            )}
            <li>
              <button className="logout-button" onClick={handleLogout}>
                <FaSignOutAlt /> Salir
              </button>
            </li>
          </ul>
        </div>
      </aside>

      <main
        className={`main-content ${isWide ? "wide" : ""} ${showAppBg ? "with-bg" : ""}`}
        style={{ "--app-bg": `url(${appBg})` }}
        onClick={() => sidebarOpen && setSidebarOpen(false)}
      >
        <Outlet />
      </main>
    </div>
  );
};

export default AppLayout;
